import { Button, TableItem } from "components/atoms";
import { Table } from "components/organisms";
import TopBar from "components/organisms/top-bar/TopBar";
import { TopbarPage } from "components/templates";
import { useFormikContext } from "formik";
import { IFormikContextType } from "hooks/useFormikField";
import { useNewWindowNavigate } from "hooks/useNewWindowNavigate";
import { useParamId } from "hooks/useParamId";
import { usePermissionGuard } from "hooks/usePermissionGuard";
import { groupContractsByTrack } from "middleware/music-product-physical/settlementInfoQueryToForm";
import { useTranslation } from "react-i18next";
import { PiCaretLeftBold, PiNavigationArrow } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { useGetMusicProductPhysicalContractHistory } from "services/music-product-physical/settlementInfo";
import { IMusicProductPhysicalForm } from "services/music-product-physical/types/form";
import { formatDate } from "utils/global/date.utils";

const MusicProductPhysicalContractHistory = () => {
  const id = useParamId();

  usePermissionGuard('ContractPermissionsLIST**');

  const { status }: IFormikContextType<IMusicProductPhysicalForm> =
    useFormikContext<IMusicProductPhysicalForm>();

  const navigateToNewWindow = useNewWindowNavigate();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const history = useGetMusicProductPhysicalContractHistory(id);

  const contractsByTrack = groupContractsByTrack(
    history.data?.settlementInfo?.perTrackContracts ?? []
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          size="compact"
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(
                  status?.displayMode
                    ? `/music-product-physical/${id}/contracts`
                    : `/music-product-physical/edit/${id}/contracts`
                )
              }
            >
                            <Button.Description description={t('button.back')} />

                            <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
          title={
            history.data?.settlementInfo
              ? `${t("input.label.per-track-settlement")}: ${
                  history.data.settlementInfo.perTrackSettlement
                    ? t("input.option.yes")
                    : t("input.option.no")
                }`
              : undefined
          }
        />
      }
    >
      <div className="flex flex-col-reverse gap-8 w-full">
        <div className="flex flex-col-reverse mb-20 w-full gap-2">
          {contractsByTrack.reverse().map((track, trackIndex) => (
            <Table
              id='MusicProductPhysicalContractHistory2'
              key={trackIndex}
              size="compact"
              data={{
                nodes: track.contracts.map((c) => ({ ...c, id: c.contractId })),
              }}
              columns={[
                {
                  label: `${track.title}`,
                  renderCell: ({ labelName: name }) => (
                    <TableItem>
                      <TableItem.Text text={name} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.contract`),
                  renderCell: (contract) => (
                    <TableItem>
                      <TableItem.Text text={contract.contractName} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.startDate'),
                  renderCell: (contract) => (
                    <TableItem>
                      <TableItem.Text
                        text={`${formatDate(contract.startDate)}`}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.endDate'),
                  renderCell: (contract) => (
                    <TableItem>
                      <TableItem.Text
                        text={
                          contract.endDate
                            ? formatDate(contract.endDate)
                            : '-'
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.division`),
                  renderCell: ({ divisionPercentage, id: index }) => (
                    <TableItem>
                      <TableItem.Text text={`${divisionPercentage ?? "-"}%`} />
                    </TableItem>
                  ),
                },

                {
                  label: "",
                  renderCell: ({ id: index, labelId, contractId }) => (
                    <TableItem justifyPosition="end">
                      <Button
                        isClear
                        onlyIcon
                        onClick={() =>
                          navigateToNewWindow(
                            `label/${labelId}/contract/${contractId}/form`
                          )
                        }
                      >
                                                <Button.Description description={t('button.go-to-form')} />

                                                <Button.Icon
                          icon={<PiNavigationArrow transform="scale(-1,1)" />}
                        />
                      </Button>
                    </TableItem>
                  ),
                },
              ]}
            />
          ))}
        </div>
        <div className="flex w-full">
          <Table
            id='MusicProductPhysicalContractHistory2'
            size="compact"
            loading={history.isLoading}
            data={{
              nodes:
                history.data?.settlementInfo?.contracts.map((c) => ({
                  ...c,
                  id: c.contractId,
                })) ?? [],
            }}
            columns={[
              {
                label: `${t("input.label.global")}`,
                renderCell: ({ labelName: name }) => (
                  <TableItem>
                    <TableItem.Text text={name} />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t(`input.label.contract`),
                renderCell: (contract) => (
                  <TableItem>
                    <TableItem.Text text={contract.contractName} />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t('input.label.startDate'),
                renderCell: (contract) => (
                  <TableItem>
                    <TableItem.Text
                      text={`${formatDate(contract.startDate)}`}
                    />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t('input.label.endDate'),
                renderCell: (contract) => (
                  <TableItem>
                    <TableItem.Text
                      text={
                        contract.endDate
                          ? formatDate(contract.endDate)
                          : '-'
                      }
                    />
                  </TableItem>
                ),
                resize: true,
              },

              {
                label: t(`input.label.division`),
                renderCell: ({ divisionPercentage, id: index }) => (
                  <TableItem>
                    <TableItem.Text text={`${divisionPercentage ?? "-"}%`} />
                  </TableItem>
                ),
              },
              {
                label: "",
                renderCell: ({ id: index, labelId, contractId }) => (
                  <TableItem justifyPosition="end">
                    <Button
                      isClear
                      onlyIcon
                      onClick={() =>
                        navigateToNewWindow(
                          `label/${labelId}/contract/${contractId}/form`
                        )
                      }
                    >
                                            <Button.Description description={t('button.go-to-form')} />

                                            <Button.Icon
                        icon={<PiNavigationArrow transform="scale(-1,1)" />}
                      />
                    </Button>
                  </TableItem>
                ),
              },
            ]}
          />
        </div>
      </div>
    </TopbarPage>
  );
};

export default MusicProductPhysicalContractHistory;
